import { useCountUp } from './chartPrimitives';

export type KpiTone = 'pass' | 'warn' | 'fail' | 'neutral' | 'info';

interface KpiTileProps {
  label: string;
  /** Pre-formatted display value. Pass `numeric` too for the count-up. */
  value: string;
  numeric?: number | null;
  /** Decimal places to hold while counting up, so the width doesn't jump. */
  precision?: number;
  unit?: string;
  sub?: string;
  tone?: KpiTone;
  /** Small trend series, newest last. Rendered as a sparkline. */
  spark?: number[];
  onClick?: () => void;
}

const TONE_COLOUR: Record<KpiTone, string> = {
  pass: 'var(--status-pass)',
  warn: 'var(--status-warn)',
  fail: 'var(--status-fail)',
  neutral: 'var(--text-primary)',
  info: 'var(--mr-vivid-blue)',
};

function Sparkline({ values, colour }: { values: number[]; colour: string }) {
  if (values.length < 2) return null;

  const width = 88;
  const height = 24;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;

  const points = values.map((value, index) => {
    const x = (index / (values.length - 1)) * width;
    const y = height - ((value - min) / span) * (height - 3) - 1.5;
    return [x, y] as const;
  });

  const line = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${width},${height} L0,${height} Z`;
  const [lastX, lastY] = points[points.length - 1];
  const gradientId = `spark-${colour.replace(/[^a-z0-9]/gi, '')}`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={colour} stopOpacity="0.22" />
          <stop offset="100%" stopColor={colour} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradientId})`} />
      <path d={line} fill="none" stroke={colour} strokeWidth="1.4" strokeLinejoin="round" />
      <circle cx={lastX} cy={lastY} r="2" fill={colour} />
    </svg>
  );
}

export function KpiTile({
  label,
  value,
  numeric,
  precision = 0,
  unit,
  sub,
  tone = 'neutral',
  spark,
  onClick,
}: KpiTileProps) {
  const shouldAnimate = typeof numeric === 'number' && Number.isFinite(numeric);
  const animated = useCountUp(shouldAnimate ? numeric : 0);
  const colour = TONE_COLOUR[tone];

  // While counting, show the interpolated number; land on the caller's
  // formatted string so units and symbols are never mangled.
  const display = shouldAnimate
    ? Math.abs(animated - (numeric as number)) < Math.pow(10, -precision) / 2
      ? value
      : animated.toFixed(precision)
    : value;

  const Element = onClick ? 'button' : 'div';

  return (
    <Element
      className="kpi-tile"
      data-interactive={onClick ? 'true' : 'false'}
      onClick={onClick}
      type={onClick ? 'button' : undefined}
    >
      <span className="kpi-label">{label}</span>

      <div className="flex items-end justify-between gap-2">
        <span className="kpi-value tnum" style={{ color: colour }}>
          {display}
          {unit && <span className="kpi-unit">{unit}</span>}
        </span>
        {spark && spark.length > 1 && <Sparkline values={spark} colour={colour} />}
      </div>

      {sub && <span className="kpi-sub">{sub}</span>}
    </Element>
  );
}
